
import { ShieldCheck, AlertTriangle, Calendar } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { VerificationRequestButton } from "@/components/host/VerificationRequestButton";
import { motion } from "framer-motion";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { RatingStars } from "@/components/host/ratings/RatingStars";

interface VerificationSectionProps {
  isVerified: boolean;
  verificationDate?: string;
  hostId: string;
  sessionUserId: string;
  googleMapsRating?: number;
  googleMapsReviewsCount?: number;
  trustpilotRating?: number;
  trustpilotReviewsCount?: number;
}

export const VerificationSection = ({
  isVerified,
  verificationDate,
  hostId,
  sessionUserId,
  googleMapsRating,
  googleMapsReviewsCount,
  trustpilotRating,
  trustpilotReviewsCount
}: VerificationSectionProps) => {
  return (
    <motion.div 
      className={`p-6 rounded-lg transition-colors shadow-sm ${
        isVerified 
          ? 'bg-gradient-to-br from-green-50 to-green-100 border border-green-200' 
          : 'bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200'
      }`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="flex items-start gap-4">
        {isVerified ? (
          <>
            <div className="rounded-full bg-white p-3 shadow-sm">
              <ShieldCheck className="w-6 h-6 text-green-500" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="font-semibold text-green-800 text-lg">Verified Host</h3>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge variant="secondary" className="bg-green-100 text-green-700 hover:bg-green-200 transition-colors">
                        Trusted
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-sm">This host has been verified by our team</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              
              <p className="text-green-700 text-sm mb-4">
                {verificationDate && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" /> 
                    Verified since {new Date(verificationDate).toLocaleDateString()}
                  </span>
                )}
              </p>

              {(googleMapsRating || trustpilotRating) && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-green-800">External Ratings</h4>
                  <div className="flex flex-wrap gap-2">
                    {googleMapsRating && (
                      <Badge variant="secondary" className="bg-white shadow-sm flex items-center gap-1.5 py-1.5 px-2.5">
                        <img src="https://www.google.com/favicon.ico" alt="Google" className="w-4 h-4" />
                        <span className="font-medium text-gray-800">{googleMapsRating.toFixed(1)}</span>
                        <RatingStars rating={googleMapsRating} size="sm" />
                        <span className="text-gray-500 text-xs">
                          ({googleMapsReviewsCount} reviews)
                        </span>
                      </Badge>
                    )}
                    {trustpilotRating && (
                      <Badge variant="secondary" className="bg-white shadow-sm flex items-center gap-1.5 py-1.5 px-2.5">
                        <div className="w-4 h-4 bg-[#00b67a] rounded-sm flex items-center justify-center">
                          <span className="text-white text-[8px] font-bold">T</span>
                        </div>
                        <span className="font-medium text-gray-800">{trustpilotRating.toFixed(1)}</span>
                        <RatingStars rating={trustpilotRating} size="sm" />
                        <span className="text-gray-500 text-xs">
                          ({trustpilotReviewsCount} reviews)
                        </span>
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="rounded-full bg-white p-3 shadow-sm">
              <AlertTriangle className="w-6 h-6 text-amber-500" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="font-semibold text-amber-800 text-lg">Verification Required</h3>
                <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                  Pending
                </Badge>
              </div>
              <p className="text-amber-700 text-sm mb-4">
                Get verified to build trust with travelers and increase your visibility.
              </p>
              <VerificationRequestButton 
                hostId={hostId} 
                isVerified={isVerified}
                sessionUserId={sessionUserId}
              />
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
};
